// Presenter tour content — the 5-act story used by /demo, the floating
// presenter bar and the /pitch-deck. Numbers here are ones already true
// of the product (career registry size, NSDC catalogue size).

export type Persona = {
  id: string;
  name: string;
  age: string;
  story: string;
  highlight: string;
};

export const PERSONAS: Persona[] = [
  {
    id: "student",
    name: "School student",
    age: "15–18",
    story: "Choosing subjects and wondering which careers fit.",
    highlight: "Show the free quiz, the Career DNA™ and the parent view of Next Steps.",
  },
  {
    id: "switcher",
    name: "Career switcher",
    age: "25–35",
    story: "Five years in one field and ready for a change.",
    highlight: "Show matched roles that value past experience, salary growth and NSDC certifications.",
  },
];

export type Act = {
  n: number;
  title: string;
  minutes: number;
  route: string;
  stops: { label: string; route: string }[];
  say: string[];
};

export const ACTS: Act[] = [
  {
    n: 1,
    title: "The hook",
    minutes: 2,
    route: "/free/career-quiz?src=demo",
    stops: [
      { label: "Homepage", route: "/" },
      { label: "Free career quiz", route: "/free/career-quiz?src=demo" },
    ],
    say: [
      "Eight quick questions. Under two minutes.",
      "An instant snapshot of who you are — no right answers.",
      "Every sign-up is tagged by where it came from and sent to the CRM.",
    ],
  },
  {
    n: 2,
    title: "Career DNA™ and 5,877 roles",
    minutes: 3,
    route: "/hub/dna",
    stops: [
      { label: "Career DNA™", route: "/hub/dna" },
      { label: "Report", route: "/report" },
      { label: "Career explorer", route: "/careers" },
    ],
    say: [
      "Seven strands, not a four-letter label.",
      "Matched against 5,877 roles mapped to ISCO-08, O*NET and ESCO.",
      "Every match explains why, with confidence and how to raise it.",
    ],
  },
  {
    n: 3,
    title: "From insight to action",
    minutes: 2,
    route: "/next-steps",
    stops: [
      { label: "Next Steps", route: "/next-steps" },
      { label: "NSDC certifications", route: "/certifications" },
    ],
    say: [
      "Month-by-month actions built from the learner's own evidence.",
      "222 NSDC-approved certifications mapped to matched careers.",
      "Save to plan in one tap.",
    ],
  },
  {
    n: 4,
    title: "Habit and momentum",
    minutes: 2,
    route: "/home",
    stops: [
      { label: "Home", route: "/home" },
      { label: "Vision Board", route: "/vision-board" },
      { label: "Rewards", route: "/rewards" },
    ],
    say: [
      "Daily Momentum™ diary with reminders and calendar sync.",
      "Pin roles to the Vision Board and track milestones.",
      "Points and badges unlock tools, retakes and courses.",
    ],
  },
  {
    n: 5,
    title: "Trust and proof",
    minutes: 1,
    route: "/parent-guide",
    stops: [
      { label: "Parent guide", route: "/parent-guide" },
      { label: "Clarity Card", route: "/hub/dna" },
      { label: "Schools (school login)", route: "/schools" },
    ],
    say: [
      "Parental consent built in for learners under 18 (DPDP).",
      "Cohort views and printable briefs for schools.",
      "Shareable Clarity Cards for families and LinkedIn.",
    ],
  },
];

export const DEMO_KEY = "wekit.demo.tour";

export type DemoState = { act: number; startedAt: number } | null;

export function readDemo(): DemoState {
  try {
    const raw = localStorage.getItem(DEMO_KEY);
    return raw ? (JSON.parse(raw) as DemoState) : null;
  } catch {
    return null;
  }
}

export function writeDemo(s: DemoState) {
  try {
    if (s) localStorage.setItem(DEMO_KEY, JSON.stringify(s));
    else localStorage.removeItem(DEMO_KEY);
    window.dispatchEvent(new Event("wekit-demo"));
  } catch {
    /* storage unavailable */
  }
}
